import { defineStore } from 'pinia'
import api from '@/services/api'

function getSelectedProjectId() {
  const raw = sessionStorage.getItem('selectedProject')
  if (!raw) return null
  try {
    const p = JSON.parse(raw)
    const id = (p && p.id) != null ? String(p.id).trim() : ''
    return id.length > 0 ? id : null
  } catch {
    return null
  }
}

function mapAddress(addr) {
  const line1 = (addr.address_line_1 || '').trim()
  const line2 = (addr.address_line_2 || '').trim()
  const formatted_address = addr.formatted_address != null
    ? addr.formatted_address
    : (line2 ? `${line1} ${line2}`.trim() : line1)
  return {
    id: addr.id,
    address_line_1: addr.address_line_1 || '',
    address_line_2: addr.address_line_2 || '',
    formatted_address,
    city: addr.city || '',
    state: addr.state || '',
    postal_code: addr.postal_code || '',
    country: addr.country || '',
    is_default: addr.is_default ?? addr.is_shipping,
    isDefault: addr.is_default ?? addr.is_shipping,
    firstName: addr.firstName || '',
    lastName: addr.lastName || '',
    province_id: addr.province_id,
    country_id: addr.country_id
  }
}

export const useAddressStore = defineStore('address', {
  state: () => ({
    addresses: [],
    loading: false,
    error: null
  }),

  actions: {
    /**
     * Fetches the client's addresses linked to the selected PROJECT (project_id → client_id → empire_client_address llivr=true).
     * If there is no selected project, uses user client_id (legacy behavior).
     * @param {string|null} [projectIdOverride] - If provided, uses this project_id (avoids relying only on sessionStorage).
     */
    async fetchAddresses(projectIdOverride = null) {
      this.loading = true
      this.error = null
      const projectId = (projectIdOverride != null && String(projectIdOverride).trim()) ? String(projectIdOverride).trim() : getSelectedProjectId()
      const hasProjectId = typeof projectId === 'string' && projectId.trim().length > 0

      try {
        const params = hasProjectId ? { project_id: projectId.trim() } : {}
        const response = await api.get('/users/client-addresses', { params })
        if (response.data && Array.isArray(response.data)) {
          console.log('📍 Endereços do cliente:', response.data.length, hasProjectId ? `(projeto ${projectId})` : '(usuário)')
          this.addresses = response.data.map(addr => mapAddress(addr))
          return
        }
        // Empty list: keep [] when searched by project; fallback only when there was no project_id
        if (hasProjectId) {
          this.addresses = []
          return
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}')
        if (!user.id) throw new Error('User not found')
        console.log('📍 Fallback: endereços do usuário (sem projeto selecionado)')
        const fallbackResponse = await api.get(`/users/${user.id}/addresses`)
        this.addresses = (fallbackResponse.data || []).map(addr => mapAddress({
          ...addr,
          address_line_1: addr.address_line_1 || addr.address || '',
          address_line_2: addr.address_line_2 || addr.landmark || ''
        }))
      } catch (error) {
        console.error('Error fetching addresses:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetches the client's default address (by selected project or user).
     * @param {string|null} [projectIdOverride] - If provided, uses this project_id.
     */
    async fetchDefaultAddress(projectIdOverride = null) {
      const projectId = (projectIdOverride != null && String(projectIdOverride).trim()) ? String(projectIdOverride).trim() : getSelectedProjectId()
      const hasProjectId = projectId != null && projectId.length > 0
      try {
        const params = hasProjectId ? { project_id: projectId } : {}
        const response = await api.get('/users/client-addresses/default', { params })
        if (response.data) {
          console.log('📍 Endereço padrão do cliente encontrado:', response.data.id, projectId ? `(projeto ${projectId})` : '')
          return {
            ...mapAddress(response.data),
            is_default: true,
            isDefault: true
          }
        }
        return null
      } catch (error) {
        console.warn('⚠️ Erro ao buscar endereço padrão do cliente:', error.message)
        return null
      }
    }
  }
})
